import { Component, OnInit, Input, OnDestroy, OnChanges, SimpleChanges } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { Subscription } from 'rxjs';

import { ProjektaufgabeService, Projektaufgabe } from '../services/projektaufgabe.service';
import { GenerateKontrolltermineDialogComponent } from './generate-kontrolltermine-dialog/generate-kontrolltermine-dialog.component';
import { PaginationService } from '../core/services/pagination.service';
import { environment } from '../../environments/environment';

@Component({
  selector: 'app-projektaufgabe',
  templateUrl: './projektaufgabe.component.html',
  styleUrls: ['./projektaufgabe.component.scss']
})
export class ProjektaufgabeComponent implements OnInit, OnDestroy, OnChanges {
  @Input() standortId?: string;
  @Input() kundeId?: string;
  
  projektaufgaben: Projektaufgabe[] = [];
  isLoading = false;
  searchTerm = '';
  
  // Pagination
  currentPage = 1;
  pageSize: number;
  pageSizeOptions: number[];
  totalItems = 0;
  totalPages = 0;
  
  // Filters
  selectedKundeId = '';
  selectedTechnikerId = '';         // replaces verantwortlicher
  selectedEinsatztyp = '';          // from environment
  selectedPeriodizitaet = '';       // from environment
  selectedStandortId = '';          // from environment

  // Dropdown data
  kunden: any[] = [];
  mitarbeiter: any[] = [];
  einsatztypOptions: any[] = [];    // from environment
  periodizitaetOptions: any[] = []; // from environment
  standorte: any[] = [];            // from environment

  // Table columns
  displayedColumns = ['beschreibung', 'techniker', 'status', 'einsatztyp', 'periodizitaet', 'actions'];
  
  // Math object for template
  Math = Math;
  
  // Active projektaufgabe (only one can be active per standort)
  activeProjektaufgabe: any = null;

  // Autocomplete properties for Kunde and Verantwortlicher
  selectedKunde: any = null;
  selectedVerantwortlicher: any = null;
  
  showKundeAutocomplete: boolean = false;
  showVerantwortlicherAutocomplete: boolean = false;
  
  kundeSearchValue: string = '';
  verantwortlicherSearchValue: string = '';
  
  filteredKunden: any[] = [];
  filteredVerantwortlicher: any[] = [];
  
  private destroy$ = new Subscription();

  constructor(
    private projektaufgabeService: ProjektaufgabeService,
    private router: Router,
    private route: ActivatedRoute,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    private paginationService: PaginationService
  ) {
    // Initialize pagination from service
    this.pageSize = this.paginationService.getPageSize();
    this.pageSizeOptions = this.paginationService.getPageSizeOptions();
  }

  ngOnInit() {
    console.log('ProjektaufgabeComponent initialized with standortId:', this.standortId);
    this.loadProjektaufgaben();
    this.loadDropdowns();
    this.loadActiveProjektaufgabe();
    this.initializeEnvironmentOptions();
  }

  ngOnDestroy() {
    this.destroy$.unsubscribe();
  }

  /**
   * Load dropdown data for filters
   */
  loadDropdowns() {
    this.projektaufgabeService.getProjektaufgabeDropdowns().subscribe({
      next: (response) => {
        this.kunden = response.kunden || [];
        this.mitarbeiter = response.mitarbeiter || [];
      },
      error: (error) => {
        console.error('Error loading dropdowns:', error);
      }
    });
  }

  /**
   * Initialize options from environment configuration
   */
  initializeEnvironmentOptions() {
    this.einsatztypOptions = environment.projektaufgabe_einsatztyp || [];
    this.periodizitaetOptions = environment.projektaufgabe_periodizitaet || [];
  }

  /**
   * Load the active projektaufgabe for the current standort
   */
  loadActiveProjektaufgabe() {
    if (!this.standortId) {
      this.activeProjektaufgabe = null;
      return;
    }

    this.projektaufgabeService.getActiveProjektaufgabe(this.standortId).subscribe({
      next: (response) => {
        this.activeProjektaufgabe = response.data || null;
        console.log('Active projektaufgabe loaded:', this.activeProjektaufgabe);
      },
      error: (error) => {
        console.error('Error loading active projektaufgabe:', error);
        this.activeProjektaufgabe = null;
      }
    });
  }

  /**
   * Refresh all data (projektaufgaben and active projektaufgabe)
   */
  refreshData() {
    this.loadProjektaufgaben();
    this.loadActiveProjektaufgabe();
  }

  /**
   * Refresh active projektaufgabe when standortId changes
   */
  ngOnChanges(changes: SimpleChanges) {
    if (changes['standortId'] && !changes['standortId'].firstChange) {
      this.loadActiveProjektaufgabe();
    }
  }

  /**
   * Load projektaufgaben for the current standort or all if no standortId
   */
  loadProjektaufgaben() {
    this.isLoading = true;
    
    // If no standortId, load all projektaufgaben (standalone mode)
    // If standortId is provided, load only for that standort (embedded mode)
    const standortIdParam = this.standortId || '';
    
    console.log('Loading projektaufgaben with params:', {
      page: this.currentPage,
      limit: this.pageSize,
      search: this.searchTerm,
      kunde_id: this.selectedKundeId,
      standort_id: standortIdParam,
      techniker_id: this.selectedTechnikerId
    });
    
    this.projektaufgabeService.getProjektaufgaben(
      this.currentPage,
      this.pageSize,
      this.searchTerm,
      this.selectedKundeId,
      standortIdParam,
      this.selectedTechnikerId
    ).subscribe({
      next: (response) => {
        this.projektaufgaben = response.data || [];
        this.totalItems = response.pagination?.total || 0;
        this.totalPages = response.pagination?.last_page || 0;
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error loading projektaufgaben:', error);
        this.snackBar.open('Fehler beim Laden der Projektaufgaben', 'Schließen', { duration: 3000 });
        this.isLoading = false;
        // Set empty arrays on error to prevent showing empty state
        this.projektaufgaben = [];
        this.totalItems = 0;
        this.totalPages = 0;
      }
    });
  }

  /**
   * Search projektaufgaben
   */
  onSearch() {
    this.currentPage = 1;
    this.loadProjektaufgaben();
  }

  /**
   * Clear search
   */
  clearSearch() {
    this.searchTerm = '';
    this.currentPage = 1;
    this.loadProjektaufgaben();
  }

  /**
   * Apply filters
   */
  applyFilters() {
    this.currentPage = 1;
    this.loadProjektaufgaben();
  }

  /**
   * Clear all filters
   */
  clearFilters() {
    this.selectedKundeId = '';
    this.selectedTechnikerId = '';
    this.selectedKunde = null;
    this.selectedVerantwortlicher = null;
    this.kundeSearchValue = '';
    this.verantwortlicherSearchValue = '';
    this.currentPage = 1;
    this.loadProjektaufgaben();
  }

  /**
   * Change page
   */
  onPageChange(event: any) {
    this.currentPage = event.pageIndex + 1;
    this.pageSize = event.pageSize;
    this.paginationService.setPageSize(event.pageSize);
    this.loadProjektaufgaben();
  }

  /**
   * Create new projektaufgabe
   */
  createProjektaufgabe() {
    if (!this.standortId) {
      this.snackBar.open('Kein Standort ausgewählt', 'Schließen', { duration: 3000 });
      return;
    }

    // Navigate to projektaufgabe-edit component in create mode (like plan-edit)
    this.router.navigate(['/projektaufgabe-edit', 'new'], {
      queryParams: {
        standort_id: this.standortId,
        kunde_id: this.kundeId || undefined,
        mode: 'create',
        parentContext: 'standort-edit',
        returnTo: 'standort-edit',
        standortUuid: this.standortId
      }
    });
  }

  /**
   * Edit projektaufgabe
   */
  editProjektaufgabe(projektaufgabe: Projektaufgabe) {
    this.router.navigate(['/projektaufgabe-edit', projektaufgabe.id]);
  }

  /**
   * Delete projektaufgabe
   */
  deleteProjektaufgabe(projektaufgabe: Projektaufgabe) {
    // TODO: Implement delete confirmation
    this.snackBar.open(`Löschen: ${projektaufgabe.beschreibung}`, 'Schließen', { duration: 2000 });
  }

  /**
   * View projektaufgabe details
   */
  viewProjektaufgabe(projektaufgabe: Projektaufgabe) {
    // TODO: Implement view dialog or navigation
    this.snackBar.open(`Details: ${projektaufgabe.beschreibung}`, 'Schließen', { duration: 2000 });
  }

  /**
   * Get CSS class for status badge
   */
  getStatusClass(statusCode?: string): string {
    if (!statusCode) return 'status-badge inactive';
    
    switch (statusCode.toLowerCase()) {
      case 'active':
      case 'aktiv':
        return 'status-badge active';
      case 'completed':
      case 'abgeschlossen':
        return 'status-badge completed';
      case 'pending':
      case 'ausstehend':
        return 'status-badge pending';
      default:
        return 'status-badge inactive';
    }
  }

  // Kunde Autocomplete Methods
  onKundeSearch(event: any) {
    const searchTerm = event.target.value.toLowerCase();
    this.kundeSearchValue = searchTerm;
    
    if (searchTerm.length === 0) {
      this.filteredKunden = this.kunden.slice(0, 10);
    } else {
      this.filteredKunden = this.kunden.filter(kunde => 
        (kunde.suchbegriff && kunde.suchbegriff.toLowerCase().includes(searchTerm)) ||
        (kunde.name && kunde.name.toLowerCase().includes(searchTerm))
      ).slice(0, 10);
    }
    
    this.showKundeAutocomplete = true;
  }

  onKundeBlur() {
    setTimeout(() => {
      this.showKundeAutocomplete = false;
    }, 200);
  }

  selectKunde(kunde: any) {
    this.selectedKunde = kunde;
    this.selectedKundeId = kunde.id;
    this.kundeSearchValue = kunde.suchbegriff || kunde.name;
    this.showKundeAutocomplete = false;
    this.applyFilters();
  }

  clearKundeFilter() {
    this.selectedKunde = null;
    this.selectedKundeId = '';
    this.kundeSearchValue = '';
    this.applyFilters();
  }

  getKundeDisplayValue(): string {
    if (this.selectedKunde) {
      return this.selectedKunde.suchbegriff || this.selectedKunde.name;
    }
    return this.kundeSearchValue;
  }

  // Verantwortlicher Autocomplete Methods
  onVerantwortlicherSearch(event: any) {
    const searchTerm = event.target.value.toLowerCase();
    this.verantwortlicherSearchValue = searchTerm;
    
    if (searchTerm.length === 0) {
      this.filteredVerantwortlicher = this.mitarbeiter.slice(0, 10);
    } else {
      this.filteredVerantwortlicher = this.mitarbeiter.filter(mitarbeiter => 
        (mitarbeiter.vorname && mitarbeiter.vorname.toLowerCase().includes(searchTerm)) ||
        (mitarbeiter.nachname && mitarbeiter.nachname.toLowerCase().includes(searchTerm)) ||
        (mitarbeiter.name && mitarbeiter.name.toLowerCase().includes(searchTerm))
      ).slice(0, 10);
    }
    
    this.showVerantwortlicherAutocomplete = true;
  }

  onVerantwortlicherBlur() {
    setTimeout(() => {
      this.showVerantwortlicherAutocomplete = false;
    }, 200);
  }

  selectVerantwortlicher(mitarbeiter: any) {
    this.selectedVerantwortlicher = mitarbeiter;
    this.selectedTechnikerId = mitarbeiter.id;
    this.verantwortlicherSearchValue = `${mitarbeiter.vorname} ${mitarbeiter.nachname}`;
    this.showVerantwortlicherAutocomplete = false;
    this.applyFilters();
  }

  clearVerantwortlicherFilter() {
    this.selectedVerantwortlicher = null;
    this.selectedTechnikerId = '';
    this.verantwortlicherSearchValue = '';
    this.applyFilters();
  }

  getVerantwortlicherDisplayValue(): string {
    if (this.selectedVerantwortlicher) {
      return `${this.selectedVerantwortlicher.vorname} ${this.selectedVerantwortlicher.nachname}`;
    }
    return this.verantwortlicherSearchValue;
  }



  getActivationStatusClasses(isActive: boolean): string {
    if (isActive) {
      return 'bg-blue-50 text-blue-700 ring-blue-600/20 dark:bg-blue-400/10 dark:text-blue-400 dark:ring-blue-400/20';
    } else {
      return 'bg-gray-50 text-gray-600 ring-gray-500/10 dark:bg-gray-400/10 dark:text-gray-400 dark:ring-gray-400/20';
    }
  }

  activateProjektaufgabe(projektaufgabe: Projektaufgabe): void {
    if (!projektaufgabe.id) {
      this.snackBar.open('Projektaufgabe ID nicht gefunden', 'Schließen', { duration: 3000 });
      return;
    }

    this.projektaufgabeService.activateProjektaufgabe(projektaufgabe.id).subscribe({
      next: (response) => {
        this.snackBar.open('Projektaufgabe erfolgreich aktiviert', 'Schließen', { duration: 3000 });
        this.loadProjektaufgaben(); // Reload to update the table
        this.loadActiveProjektaufgabe(); // Reload to update the active projektaufgabe div
      },
      error: (error) => {
        console.error('Error activating projektaufgabe:', error);
        const errorMessage = error.error?.message || 'Fehler beim Aktivieren der Projektaufgabe';
        this.snackBar.open(errorMessage, 'Schließen', { duration: 5000 });
      }
    });
  }

  deactivateProjektaufgabe(projektaufgabe: Projektaufgabe): void {
    if (!projektaufgabe.id) {
      this.snackBar.open('Projektaufgabe ID nicht gefunden', 'Schließen', { duration: 3000 });
      return;
    }

    this.projektaufgabeService.deactivateProjektaufgabe(projektaufgabe.id).subscribe({
      next: (response) => {
        this.snackBar.open('Projektaufgabe erfolgreich deaktiviert', 'Schließen', { duration: 3000 });
        this.loadProjektaufgaben(); // Reload to update the table
        this.loadActiveProjektaufgabe(); // Reload to update the active projektaufgabe div
      },
      error: (error) => {
        console.error('Error deactivating projektaufgabe:', error);
        const errorMessage = error.error?.message || 'Fehler beim Deaktivieren der Projektaufgabe';
        this.snackBar.open(errorMessage, 'Schließen', { duration: 5000 });
      }
    });
  }

  /**
   * Get the display label for Einsatztyp
   */
  getEinsatztypLabel(einsatztyp: string): string {
    if (!einsatztyp) return '-';
    
    const option = this.einsatztypOptions.find(opt => opt.value === einsatztyp);
    return option ? option.label : einsatztyp;
  }

  /**
   * Get the display label for Periodizität
   */
  getPeriodizitaetLabel(periodizitaet: number | string): string {
    if (!periodizitaet) return '-';
    
    // Convert to number if it's a string
    const periodizitaetId = typeof periodizitaet === 'string' ? parseInt(periodizitaet) : periodizitaet;
    
    const option = this.periodizitaetOptions.find(opt => opt.id === periodizitaetId);
    return option ? option.label : periodizitaet.toString();
  }

  /**
   * Get the display label for Periodizität Serie
   * Returns formatted text for gerade/ungerade or erstes/zweites/drittes
   */
  getPeriodizitatSerieLabel(periodizitatSerie: string | undefined): string {
    if (!periodizitatSerie) return '';
    
    if (periodizitatSerie === 'gerade') {
      return 'Gerade Monate';
    } else if (periodizitatSerie === 'ungerade') {
      return 'Ungerade Monate';
    } else if (periodizitatSerie === 'erstes') {
      return 'Erstes Monat';
    } else if (periodizitatSerie === 'zweites') {
      return 'Zweites Monat';
    } else if (periodizitatSerie === 'drittes') {
      return 'Drittes Monat';
    }
    
    return '';
  }

  /**
   * Check if periodizitat_serie should be displayed
   * Relevant when periodizitaet = 3 (alle_2_monate) or 4 (quartalsweise)
   */
  shouldShowPeriodizitatSerie(periodizitaet: number | string | undefined): boolean {
    if (!periodizitaet) return false;
    const periodizitaetId = typeof periodizitaet === 'string' ? parseInt(periodizitaet) : periodizitaet;
    return periodizitaetId === 3 || periodizitaetId === 4; // 3 = alle_2_monate, 4 = quartalsweise
  }
}
